/**
 * Links a run to a business opportunity (optional, `idea.view`): a search over the opportunities' titles, the best
 * scored first, each found one a button; the choice shows as a line with Unlink.
 */

import { useState } from 'react';
import { Link2, Search, X } from 'lucide-react';

import { getIdeasApiIdeasGetParamsQOneMax } from '@/api/generated/schemas';
import { useIdeas } from '@/api/queries';
import type { AdvisorIdeaRef } from '@/api/types';
import { useDebouncedSearch } from '@/ui/useDebouncedSearch';

const RESULTS = 6;

function Found({ q, onPick }: { q: string; onPick: (idea: AdvisorIdeaRef) => void }) {
  const ideas = useIdeas(
    { dispositions: ['active'], industries: [], outcomes: [], q, sort: 'total', order: 'desc', page: 1, page_size: RESULTS },
    q.length >= 2,
  );
  if (q.length < 2) return null;
  if (ideas.isPending) return <p className="hint" role="status">Searching opportunities</p>;
  if (ideas.error) return <p className="hint" role="status">The search failed. Try again.</p>;
  const rows = ideas.data.ideas;
  if (rows.length === 0) return <p className="hint" role="status">No opportunity matches.</p>;
  return (
    <ul className="advisor-idea-results" aria-label="Opportunities found">
      {rows.map((idea) => (
        <li key={idea.id}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onPick({ id: idea.id, title: idea.title })}>
            <Link2 aria-hidden="true" /> {idea.title}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function IdeaPicker({
  id,
  value,
  onChange,
}: {
  id: string;
  value: AdvisorIdeaRef | null;
  onChange: (idea: AdvisorIdeaRef | null) => void;
}) {
  const [q, setQ] = useState('');
  const [text, setText] = useDebouncedSearch(q, ({ q: next }) => setQ(next));
  if (value) {
    return (
      <div className="advisor-idea-chosen">
        <span>{value.title}</span>
        <button type="button" className="icon-btn" aria-label={`Unlink ${value.title}`} title="Unlink" onClick={() => onChange(null)}>
          <X aria-hidden="true" />
        </button>
      </div>
    );
  }
  return (
    <div className="stack-sm">
      <div className="search-field">
        <Search aria-hidden="true" />
        <input
          id={id}
          type="search"
          autoComplete="off"
          maxLength={getIdeasApiIdeasGetParamsQOneMax}
          value={text}
          placeholder="Search business opportunities (optional)"
          onChange={(e) => setText(e.target.value)}
        />
      </div>
      <Found
        q={q}
        onPick={(idea) => {
          onChange(idea);
          setText('');
          setQ('');
        }}
      />
    </div>
  );
}
